import { AUTH_CONTROLS_DEFAULT_LOGOUT_HREF, type AuthControlsOptions, renderAuthControls } from 'AuthBff/auth-controls/render';
import { bootSession, type SessionBootOptions } from 'AuthBff/session-boot';
import type { AuthState } from 'AuthBff/types';
import { delegateUserAvatarMenu } from 'BomdisoftComponents/account/user-avatar-menu/delegate';

export type AuthControlsBehaviour = SessionBootOptions & { boot?: (options: SessionBootOptions) => Promise<AuthState>; onSignOut?: (logoutHref: string) => void };

const SIGNED_OUT_PARTS = ':scope > .bomdisoft-auth-buttons-sign-in, :scope > .bomdisoft-auth-buttons-sign-up, :scope > .bomdisoft-skeleton';

/**
 * Boots the page's session and, once a person is signed in, swaps the server-rendered
 * sign-in buttons among `root`'s children for their account menu, leaving the rest of
 * `root` alone. A signed-out visitor keeps the buttons, so the header never waits on
 * the network to be useful.
 */
export const delegateAuthControls = (root: HTMLElement, options: AuthControlsOptions, { boot = bootSession, onSignOut, ...bootOptions }: AuthControlsBehaviour = {}) => {
  let cancelled = false;
  let release = () => {};
  const logoutHref = options.logoutHref ?? AUTH_CONTROLS_DEFAULT_LOGOUT_HREF;
  const signOut = onSignOut ?? ((href: string) => root.ownerDocument.defaultView?.location.assign(href));
  const showSignedIn = (state: AuthState) => {
    const parts = [...root.querySelectorAll(SIGNED_OUT_PARTS)];
    const [first] = parts;
    if (cancelled || state.status !== 'authenticated' || first === undefined) return;
    const template = root.ownerDocument.createElement('template');
    template.innerHTML = renderAuthControls(options, state).value;
    first.replaceWith(template.content);
    for (const part of parts.slice(1)) part.remove();
    release = delegateUserAvatarMenu(root, { onSignOut: () => signOut(logoutHref), tooltipId: options.tooltipId ?? 'auth-controls-tooltip' });
  };
  const onSilentSignIn = () => void boot({ ...bootOptions, isCancelled: () => cancelled, silentSso: false }).then(showSignedIn);
  void boot({ ...bootOptions, isCancelled: () => cancelled, onSilentSignIn }).then(showSignedIn);
  return () => {
    cancelled = true;
    release();
  };
};
